import {
  PartnerComparisonStyledTable,
  PartnerComparisonTableInlineExpander,
  PartnerComparisonTableBody,
  PartnerComparisonTableBodyRow,
  PartnerComparisonTableBodyTD,
  PartnerComparisonTableHeadRow,
  PartnerComparisonTableHeadTD,
} from './partner_comparison_table.styles'
import { Text } from '@amzn/storm-ui'
import { EnumGroup } from '../../../../util/filter_menu_helper'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PartnerDetails } from '../../../directory/components/partner_comparison/compared_partners_context'
import { ClaimedByPartnerIcon } from '../../../directory/components/legends/legends.styles'
import { AmazonMarketplaces, BadgeType, CompanyServicesProvided, ProductsSupported } from '../../../../generated/graphql'
import { VerifiedIcon } from '../../../../shared/components/shared_components.styles'
import { directoryStrings } from '../../../directory/directory_i18n_mappings'
import { clickPartnerComparisonDetailsMetricHandler } from '../../../../util/partner_comparison_metrics_helper'

export enum PartnerOffers {
  Services = 'services',
  Products = 'products',
  Countries = 'countries',
}

interface PartnerComparisonTableProps {
  tableLabelKey: string
  tableSectionGroup: EnumGroup[]
  translationGroupTokenMap: Map<string, string>
  translationTokenMap: Map<string, string>
  partnerOffersField: PartnerOffers
  selectedPartners: PartnerDetails[]
  emptyColumnCount: number
  offerCount?: number
}

export const PartnerComparisonTable: React.FunctionComponent<PartnerComparisonTableProps> = (props: PartnerComparisonTableProps): JSX.Element => {
  const {
    tableLabelKey,
    tableSectionGroup,
    translationGroupTokenMap,
    translationTokenMap,
    partnerOffersField,
    selectedPartners,
    emptyColumnCount,
    offerCount,
  } = props
  const [isExpanderOpen, setIsExpanderOpen] = useState(true)
  const { t } = useTranslation()

  const handleClickExpander = (): void => {
    clickPartnerComparisonDetailsMetricHandler(`partnerComparison.page.${partnerOffersField}.expander.click`)
    setIsExpanderOpen(prev => !prev)
  }

  const partnerDataComparisonTable = useMemo(() => {
    const displayCompanyServiceLegend = (partner: PartnerDetails, companyService: CompanyServicesProvided, key: string): JSX.Element => {
      if (partner.services?.includes(companyService)) {
        const isServiceVerified = !!partner.badges?.services?.some(
          (badge: { value: string; type: BadgeType }) => badge.value === companyService && badge.type === BadgeType.VerifiedByAmazon,
        )
        return isServiceVerified ? (
          <VerifiedIcon role={'img'} aria-label={t(directoryStrings.VERIFIED_BY_AMAZON_ICON)} key={`verified-service-${key}-${partner.id}`} />
        ) : (
          <ClaimedByPartnerIcon
            role={'img'}
            aria-label={t(directoryStrings.CLAIMED_BY_PARTNER_ICON)}
            key={`claimed-by-partner-services-${key}-${partner.id}`}
          />
        )
      }
      return <></>
    }

    const displayProductLegend = (partner: PartnerDetails, product: ProductsSupported, key: string): JSX.Element => {
      if (partnerOffersField === PartnerOffers.Products && partner.products?.includes(product)) {
        const isProductVerified = !!partner.badges?.products?.some(
          (badge: { value: string; type: BadgeType }) => badge.value === product && badge.type === BadgeType.VerifiedByAmazon,
        )
        return isProductVerified ? (
          <VerifiedIcon role={'img'} aria-label={t(directoryStrings.VERIFIED_BY_AMAZON_ICON)} key={`verified-product-${key}-${partner.id}`} />
        ) : (
          <ClaimedByPartnerIcon
            role={'img'}
            aria-label={t(directoryStrings.CLAIMED_BY_PARTNER_ICON)}
            key={`claimed-by-partner-products-${key}-${partner.id}`}
          />
        )
      }
      return <></>
    }

    const displayMarketplaceLegend = (partner: PartnerDetails, marketPlace: AmazonMarketplaces, key: string): JSX.Element => {
      if (partner.marketplacesServed?.includes(marketPlace))
        return (
          <ClaimedByPartnerIcon
            role={'img'}
            aria-label={t(directoryStrings.CLAIMED_BY_PARTNER_ICON)}
            key={`claimed-by-partner-marketplaces-${key}-${partner.id}`}
          />
        )
      return <></>
    }

    return (
      <PartnerComparisonStyledTable>
        {tableSectionGroup.map(({ groupName, items }, index) => (
          <>
            <thead key={`${tableLabelKey}-${index}`}>
              <PartnerComparisonTableHeadRow key={`${tableLabelKey}-${index}`}>
                <PartnerComparisonTableHeadTD key={`${tableLabelKey}-${index}`}>
                  <Text key={`${tableLabelKey}-${index}`} type={'h5'}>
                    {t(translationGroupTokenMap.get(groupName) ?? '')}
                  </Text>
                </PartnerComparisonTableHeadTD>
              </PartnerComparisonTableHeadRow>
            </thead>
            <PartnerComparisonTableBody key={`${tableLabelKey}-${groupName}-${index}-index`}>
              {items.map((item, iterator: number) => (
                <PartnerComparisonTableBodyRow key={`${tableLabelKey}-${item}-${iterator}-iterator`} data-testid={`${tableLabelKey}-${item}`}>
                  <PartnerComparisonTableBodyTD key={`${tableLabelKey}-${item}-${iterator}-iterator`}>
                    <Text key={`${tableLabelKey}-${item}-${iterator}-iterator`}>{t(translationTokenMap.get(item) ?? '')}</Text>
                  </PartnerComparisonTableBodyTD>
                  {selectedPartners.map((partner, iterator2) => {
                    return (
                      <PartnerComparisonTableBodyTD key={`${tableLabelKey}-${groupName}-${item}-${iterator2}-${partner.id}`}>
                        {partnerOffersField === PartnerOffers.Services ? (
                          displayCompanyServiceLegend(partner, item as CompanyServicesProvided, `${tableLabelKey}-${groupName}-${item}-${iterator2}`)
                        ) : partnerOffersField === PartnerOffers.Products ? (
                          displayProductLegend(partner, item as ProductsSupported, `${tableLabelKey}-${groupName}-${item}-${iterator2}`)
                        ) : partnerOffersField === PartnerOffers.Countries ? (
                          displayMarketplaceLegend(partner, item as AmazonMarketplaces, `${tableLabelKey}-${groupName}-${item}-${iterator2}`)
                        ) : (
                          <></>
                        )}
                      </PartnerComparisonTableBodyTD>
                    )
                  })}
                  {emptyColumnCount > 0 && [...Array(emptyColumnCount)].map((_, i) => <PartnerComparisonTableBodyTD key={`${tableLabelKey}-${i}`} />)}
                </PartnerComparisonTableBodyRow>
              ))}
            </PartnerComparisonTableBody>
          </>
        ))}
      </PartnerComparisonStyledTable>
    )
  }, [selectedPartners, emptyColumnCount, partnerOffersField, t, tableLabelKey, tableSectionGroup, translationGroupTokenMap, translationTokenMap])

  return (
    <PartnerComparisonTableInlineExpander
      label={
        <Text type={'h3'} textColor={'base'}>
          {t(tableLabelKey, { count: offerCount })}
        </Text>
      }
      isOpen={isExpanderOpen}
      handleClick={handleClickExpander}
    >
      {partnerDataComparisonTable}
    </PartnerComparisonTableInlineExpander>
  )
}
